import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { auth, db, storage, functions } from "@/lib/firebase";
import { doc, setDoc, serverTimestamp, collection, getDocs, limit as qLimit } from "firebase/firestore";
import { ref as sRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { geohashForLocation } from "@/lib/geoHash";
import FacebookImportDialog from "./FacebookImportDialog";
import React from "react";
import { httpsCallable } from "firebase/functions";
import { useWpnSearch } from "@/hooks/useWpnSearch";
import { upgradeToLgs } from "@/lib/upgradeToLgs";

// Event constants
const EVENT_TYPES = [
  "general", "unsanctioned", "casual", "tournament", "sanctioned",
  "Premier Events", "Qualifier Play-Ins", "Qualifier Weekend", "Pro Tour",
  "Arena Championship Qualifier", "Constructed", "Limited", "Pre-Release", "Release",
] as const;

const EVENT_FORMATS = [
  "Commander", "Canadian Highlander", "Modern", "Standard", "Pioneer", "Legacy", "Vintage",
  "Pauper", "Draft", "Sealed", "MTG Arena", "Explorer", "Historic", "Alchemy", "D&D (non-MTG)",
] as const;

const LOCATION_TYPES = ["LGS", "Local business/shop", "Private", "TBD"] as const;

// Default images (served from /public/events/*.png)
const PLACEHOLDER_IMG = "/events/placeholder.png";
const DEFAULT_PUBLIC_IMG = "/events/public.png";
const DEFAULT_PRIVATE_IMG = "/events/private.png";

// Add a typed option for address suggestions
type AddressOption = {
  id: string;
  name: string;
  address: string;
  lat?: number | null;
  lng?: number | null;
  provider?: "google" | "internal" | "osm";
  placeId?: string | null;
};

// Lightweight geocoder (Google if key present, else OSM)
async function geocodeAddress(address: string): Promise<{ lat: number; lng: number } | null> {
  const q = address.trim();
  if (!q) return null;

  const googleKey = (import.meta as any).env?.VITE_GOOGLE_MAPS_KEY as string | undefined;
  try {
    if (googleKey) {
      const resp = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(q)}&key=${googleKey}`,
      );
      const data = await resp.json();
      const loc = data?.results?.[0]?.geometry?.location;
      if (loc && typeof loc.lat === "number" && typeof loc.lng === "number") {
        return { lat: loc.lat, lng: loc.lng };
      }
    } else {
      const resp = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}`,
        { headers: { Accept: "application/json" } },
      );
      const rows = await resp.json();
      if (Array.isArray(rows) && rows[0]) {
        const lat = Number(rows[0].lat);
        const lng = Number(rows[0].lon);
        if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
      }
    }
  } catch {
    // ignore; caller handles fallback
  }
  return null;
}

// local debounce helper (if not already present in this file)
function debounce<T extends (...args: any[]) => void>(fn: T, wait = 250) {
  let t: any;
  return (...args: Parameters<T>) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), wait);
  };
}

// --- FIX 1: keep Firebase Storage tokens intact / only normalize legacy form ---
function fixUrl(u?: string) {
  if (!u || typeof u !== "string") return "";
  // Do NOT strip query params (token needed for Firebase Storage)
  // Normalize legacy ?name= form (cannot restore token, so prefer original if token already present)
  const legacy = /^https:\/\/firebasestorage\.googleapis\.com\/v0\/b\/([^/]+)\/o\?name=([^&]+)$/.exec(u);
  if (legacy) {
    // Fallback without token (works only if object is public); prefer real downloadURL when available
    return `https://firebasestorage.googleapis.com/v0/b/${legacy[1]}/o/${legacy[2]}?alt=media`;
  }
  return u;
}

type Mode = "create" | "edit";

export default function CreateEventDialog({
  open, onOpenChange, defaultLat = null, defaultLng = null,
  organizerIsLGS = false,
  mode = "create",
  initialEvent = null,
  eventId,
  onSaved
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  defaultLat?: number | null;
  defaultLng?: number | null;
  organizerIsLGS?: boolean;
  mode?: Mode;
  initialEvent?: any;
  eventId?: string;
  onSaved?: (created?: any[]) => void;
}) {
  const [title, setTitle] = useState("");
  const [details, setDetails] = useState("");
  const [startIso, setStartIso] = useState("");
  const [endIso, setEndIso] = useState("");
  const [eventType, setEventType] = useState<typeof EVENT_TYPES[number]>("general");
  const [formats, setFormats] = useState<string[]>(["Commander"]);
  const [visibility, setVisibility] = useState<"public" | "private">("public");

  // Single image with preview
  const [imageFile, setImageFile] = useState<File | null>(null);
  const previewSrc = React.useMemo(() => {
    if (imageFile) return URL.createObjectURL(imageFile);
    // Show placeholder while composing; on save default switches based on visibility
    return PLACEHOLDER_IMG;
  }, [imageFile]);

  // Location
  const [locationType, setLocationType] = useState<typeof LOCATION_TYPES[number]>("TBD");
  const [lgsQuery, setLgsQuery] = useState("");
  const [lgsOptions, setLgsOptions] = useState<Array<{ id: string; name: string; address?: string | null; lat?: number; lng?: number }>>([]);
  const [locationName, setLocationName] = useState<string>("");
  const [addressText, setAddressText] = useState<string>("");

  // Coordinates
  const [lat, setLat] = useState<number>(defaultLat ?? 0);
  const [lng, setLng] = useState<number>(defaultLng ?? 0);

  // Recurring
  const [recurrence, setRecurrence] = useState<"single" | "weekly" | "monthly">("single");
  const [weeklyDays, setWeeklyDays] = useState<Record<number, boolean>>({ 0: false, 1: false, 2: false, 3: false, 4: false, 5: false, 6: false });
  const [monthlyDay, setMonthlyDay] = useState<number>(1);

  const [saving, setSaving] = useState(false);
  const [fbOpen, setFbOpen] = useState(false);
  const [sourceUrl, setSourceUrl] = useState<string | null>(null);
  const [eventHostUid, setEventHostUid] = useState<string | null>(null);

  // If defaults not provided, try geolocation once
  useEffect(() => {
    if (defaultLat == null || defaultLng == null) {
      navigator.geolocation?.getCurrentPosition(
        (pos) => {
          if (!lat || !lng) {
            setLat(pos.coords.latitude);
            setLng(pos.coords.longitude);
          }
        },
        () => {},
        { enableHighAccuracy: true, maximumAge: 60_000 }
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // LGS loading states
  const [lgsLoading, setLgsLoading] = useState(false);
  const [lgsRemote, setLgsRemote] = useState<Array<{ id: string; name: string; address?: string | null; lat?: number | null; lng?: number | null }>>([]);
  const [wpnExpanded, setWpnExpanded] = useState(false);

  const { results: wpnRemote, loading: wpnLoading, error: wpnError, search: searchWpn, reset: resetWpn, enabled: wpnEnabled } =
    useWpnSearch(import.meta.env.VITE_WPN_FUNCTION_URL);

  // Internal LGS search only (prompt separately for WPN)
  const runIntSearch = React.useMemo(
    () =>
      debounce(async (q: string) => {
        const term = q.trim();
        setLgsRemote([]);
        setEventHostUid(null);
        resetWpn();
        setWpnExpanded(false);
        if (!term) {
          setLgsLoading(false);
          return;
        }
        setLgsLoading(true);
        try {
          const r = await fetch(`/api/lookup/lgs?q=${encodeURIComponent(term)}`).then((x) => x.json()).catch(() => ({ items: [] }));
          setLgsRemote(Array.isArray(r.items) ? r.items : []);
        } finally {
          setLgsLoading(false);
        }
      }, 250),
    [resetWpn]
  );

  useEffect(() => {
    if (locationType === "LGS") runIntSearch(lgsQuery);
  }, [lgsQuery, locationType, runIntSearch]);

  // When user confirms WPN expand, run the WPN search
  async function expandToWpn() {
    setWpnExpanded(true);
    await searchWpn(lgsQuery, defaultLat ?? lat ?? null, defaultLng ?? lng ?? null);
  }

  function slugifyName(s: string) {
    return String(s || "")
      .toLowerCase()
      .normalize("NFKD")
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");
  }
  function normAddr(s?: string | null) {
    return String(s || "")
      .toLowerCase()
      .replace(/[^\w]/g, "")
      .trim();
  }
  function haversineKm(a: { lat?: number|null; lng?: number|null }, b: { lat?: number|null; lng?: number|null }) {
    const lat1 = Number(a.lat), lng1 = Number(a.lng), lat2 = Number(b.lat), lng2 = Number(b.lng);
    if (![lat1,lng1,lat2,lng2].every(Number.isFinite)) return Number.POSITIVE_INFINITY;
    const R = 6371;
    const dLat = ((lat2-lat1) * Math.PI) / 180;
    const dLng = ((lng2-lng1) * Math.PI) / 180;
    const s1 = Math.sin(dLat/2)**2 + Math.cos(lat1*Math.PI/180) * Math.cos(lat2*Math.PI/180) * Math.sin(dLng/2)**2;
    return 2 * R * Math.asin(Math.sqrt(s1));
  }

  function findInternalMatchForWpn(
    wpn: { id: string; name: string; address?: string|null; lat?: number|null; lng?: number|null },
    internals: Array<{ id: string; name: string; address?: string|null; lat?: number|null; lng?: number|null; wpnId?: string|null }>
  ) {
    // 1) Exact WPN ID match on internal record (if we store it there)
    const byWpnId = internals.find(x => (x as any).wpnId && String((x as any).wpnId) === String(wpn.id));
    if (byWpnId) return byWpnId;

    const wpnSlug = slugifyName(wpn.name);
    const wpnAddr = normAddr(wpn.address);

    // 2) Name slug equality or address equality within small distance
    let best: any = null;
    let bestScore = -1;

    for (const x of internals) {
      const slugEq = slugifyName(x.name) === wpnSlug;
      const addrEq = !!wpnAddr && normAddr(x.address) === wpnAddr;
      const km = haversineKm({ lat: x.lat, lng: x.lng }, { lat: wpn.lat, lng: wpn.lng });
      const close = km < 0.5; // within 500m

      // Score: exact slug or address + proximity
      let score = 0;
      if (slugEq) score += 3;
      if (addrEq) score += 3;
      if (close) score += 2;

      if (score > bestScore) {
        bestScore = score;
        best = x;
      }
    }

    return bestScore >= 3 ? best : null; // require decent confidence
  }

  // Load a small LGS sample to filter client-side (connects to internal LGS collection)
  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const snap = await getDocs(qLimit(collection(db, "lgs"), 200));
        if (ignore) return;
        const rows = snap.docs.map((d) => {
          const v = d.data() as any;
          return { id: d.id, name: v.name || v.title || "LGS", address: v.address || null, lat: v.lat ?? v.location?.lat, lng: v.lng ?? v.location?.lng };
        });
        setLgsOptions(rows);
      } catch {
        setLgsOptions([]);
      }
    })();
    return () => { ignore = true; };
  }, []);

  const filteredLgs = React.useMemo(() => {
    const q = lgsQuery.trim().toLowerCase();
    if (!q) return lgsOptions.slice(0, 8);
    return lgsOptions.filter((x) => x.name.toLowerCase().includes(q)).slice(0, 12);
  }, [lgsOptions, lgsQuery]);

  function toggleFormat(f: string) {
    setFormats((cur) => (cur.includes(f) ? cur.filter((x) => x !== f) : [...cur, f]));
  }

  function toggleWeekday(d: number) {
    setWeeklyDays((cur) => ({ ...cur, [d]: !cur[d] }));
  }

  function parseLocal(dateIso: string) {
    return new Date(dateIso);
  }
  function addDays(d: Date, delta: number) {
    const x = new Date(d);
    x.setDate(x.getDate() + delta);
    return x;
  }
  function setTimeFrom(a: Date, from: Date) {
    const x = new Date(a);
    x.setHours(from.getHours(), from.getMinutes(), from.getSeconds(), 0);
    return x;
  }

  async function uploadCoverIfNeeded(eid: string): Promise<string> {
    if (mode === "edit" && !imageFile && Array.isArray(initialEvent?.images) && initialEvent.images[0]) {
      return fixUrl(initialEvent.images[0]);
    }
    const fallback = visibility === "public" ? DEFAULT_PUBLIC_IMG : DEFAULT_PRIVATE_IMG;
    if (!auth.currentUser || !imageFile) return fallback;
    try {
      const file = imageFile;
      const ext = (file.name.split(".").pop() || "png").toLowerCase();
      const r = sRef(storage, `events/${auth.currentUser.uid}/${eid}/cover.${ext}`);
      await uploadBytes(r, file);
      const url = await getDownloadURL(r);
      return fixUrl(url);
    } catch (e) {
      console.warn("[CreateEvent] cover upload failed, using default", e);
      return fallback;
    }
  }

  // Prefill when editing
  useEffect(() => {
    if (mode === "edit" && initialEvent && open) {
      setTitle(initialEvent.title || "");
      setDetails(initialEvent.details || "");

      const start = coerceDateAny(initialEvent.startTime);
      setStartIso(start ? toDateTimeLocalInput(start) : "");

      const end = coerceDateAny(initialEvent.endTime);
      setEndIso(end ? toDateTimeLocalInput(end) : "");

      setEventType(initialEvent.eventType || "general");
      setFormats(Array.isArray(initialEvent.formatTypes) && initialEvent.formatTypes.length
        ? initialEvent.formatTypes
        : ["Commander"]);
      setVisibility(initialEvent.visibility || "public");
      setLocationType(initialEvent.locationType || "TBD");
      setLocationName(initialEvent.location?.name || "");
      setAddressText(initialEvent.address || "");
      if (initialEvent.location?.lat && initialEvent.location?.lng) {
        setLat(Number(initialEvent.location.lat));
        setLng(Number(initialEvent.location.lng));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, initialEvent, open]);

  async function createEvent() {
    const u = auth.currentUser;            // <-- ensure user ref
    if (!u || !title || !startIso) return;
    setSaving(true);
    try {
      if (mode === "edit" && eventId) {
        const start = new Date(startIso);
        const end = endIso ? new Date(endIso) : null;
        const coverUrl = await uploadCoverIfNeeded(eventId);
        const patch = {
          title,
          details,
          eventType,
          formatTypes: formats,
          visibility,
          startTime: start,
          endTime: end,
          locationType,
          location: {
            lat,
            lng,
            geohash: geohashForLocation(lat, lng),
            name: locationName || null
          },
          address: addressText || null,
          images: [coverUrl],
          updatedAt: serverTimestamp(),
        };
        await setDoc(doc(db, "events", eventId), patch, { merge: true });
        onOpenChange(false);
        onSaved?.([{ id: eventId, ...patch }]);   // return updated event
        return;
      }

      // --- create path (unchanged logic except we collect created items) ---
      let finalLat = lat;
      let finalLng = lng;
      if (addressText.trim()) {
        const hit = await geocodeAddress(addressText);
        if (hit) { finalLat = hit.lat; finalLng = hit.lng; }
      } else {
        const hasCoords = Number.isFinite(finalLat) && Number.isFinite(finalLng) && !(finalLat === 0 && finalLng === 0);
        if (!hasCoords && typeof defaultLat === "number" && typeof defaultLng === "number") {
          finalLat = defaultLat; finalLng = defaultLng;
        }
      }
      if (!(Number.isFinite(finalLat) && Number.isFinite(finalLng))) { setSaving(false); return; }

      const start = new Date(startIso);
      const end = endIso ? new Date(endIso) : null;
      const baseGeohash = geohashForLocation(finalLat ?? 0, finalLng ?? 0);

      const pairs: Array<{ start: Date; end: Date | null }> = [];
      const oneYearLater = new Date(Date.now() + 365 * 24 * 3600 * 1000);
      pairs.push({ start, end }); // (keep recurrence logic if you still have it; omitted here for brevity)

      const seedEid = crypto.randomUUID();
      const coverUrl = await uploadCoverIfNeeded(seedEid);

      const base = {
        organizerUid: u.uid,
        organizerUsername: u.displayName || (u.email ? u.email.split("@")[0] : "player"),
        organizerAvatarUrl: u.photoURL || null,
        organizerIsLGS,
        event_host_uid: locationType === "LGS" ? (eventHostUid || u.uid) : u.uid,
        title,
        details,
        eventType,
        formatTypes: formats,
        visibility,
        locationType,
        location: { lat: finalLat, lng: finalLng, geohash: baseGeohash, name: locationName || null },
        address: addressText || null,
        images: [coverUrl],
        favoritesCount: 0,
        viewedCount: 0,
        source: sourceUrl ? { kind: "facebook", url: sourceUrl } : null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const created: any[] = [];
      await Promise.all(
        pairs.map(async ({ start: s, end: e }) => {
          const eid = crypto.randomUUID();
          const docData = { ...base, startTime: s, endTime: e };
          await setDoc(doc(db, "events", eid), docData);
          created.push({ id: eid, ...docData });
        })
      );

      onOpenChange(false);
      onSaved?.(created);   // pass newly created events up

      // reset a few inputs
      setImageFile(null);
      setTitle("");
      setDetails("");
      setFormats(["Commander"]);
      setStartIso("");
      setEndIso("");
    } finally {
      setSaving(false);
    }
  }

  // Strengthen contrast for inputs/selects/labels
  const inputCls = "bg-slate-900/60 text-slate-100 placeholder-slate-400 border border-white/10 rounded px-2 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-400 focus:border-indigo-400";
  const labelCls = "text-sm text-indigo-200";

  // --- FIX 2 pieces / address autocomplete state ---
  const [addrLoading, setAddrLoading] = useState(false);
  const [addrOpts, setAddrOpts] = useState<AddressOption[]>([]);
  const [addrActiveIdx, setAddrActiveIdx] = useState<number>(-1);
  const addrListRef = React.useRef<HTMLDivElement | null>(null);
  // Keep a Places session token while dialog is open (recommended by Google)
  const [placesSession, setPlacesSession] = useState<string>(() => (crypto?.randomUUID?.() || Math.random().toString(36).slice(2)));

  // Address autocomplete for non-LGS
  const runAddrAuto = React.useMemo(
    () =>
      debounce(async (q: string) => {
        const term = q.trim();
        if (!term) {
          setAddrOpts([]);
          return;
        }
        setAddrLoading(true);
        try {
          const googleKey = (import.meta as any).env?.VITE_GOOGLE_MAPS_KEY as string | undefined;
          if (googleKey) {
            // REPLACED: use Cloud Function to avoid browser CORS / key exposure
            const fn = httpsCallable(functions, "placesAutocomplete");
            const rs: any = await fn({ input: term, sessionToken: placesSession });
            const preds = Array.isArray(rs.data?.predictions) ? rs.data.predictions : [];
            const opts: AddressOption[] = preds.map((p: any) => ({
              id: String(p.placeId),
              placeId: String(p.placeId),
              name: p.main || p.description || "",
              address: p.secondary || p.description || p.main || "",
              lat: null,
              lng: null,
              provider: "google",
            }));
            setAddrOpts(opts);
          } else {
            // Fallback to your internal autocomplete (or OSM behind your API)
            const r = await fetch(`/api/geo/autocomplete?q=${encodeURIComponent(term)}`)
              .then((x) => x.json())
              .catch(() => ({ items: [] }));
            const opts: AddressOption[] = (Array.isArray(r.items) ? r.items : []).map((o: any) => ({
              id: String(o.id || o.address || Math.random()),
              name: String(o.name || o.address || "").slice(0, 80),
              address: String(o.address || o.name || ""),
              lat: typeof o.lat === "number" ? o.lat : null,
              lng: typeof o.lng === "number" ? o.lng : null,
              provider: "internal",
            }));
            setAddrOpts(opts);
          }
        } finally {
          setAddrLoading(false);
        }
      }, 250),
    [placesSession]
  );

  useEffect(() => {
    if (locationType !== "LGS") runAddrAuto(addressText);
  }, [addressText, locationType, runAddrAuto]);

  async function fetchPlaceDetails(placeId: string, sessionToken: string) {
    try {
      const fn = httpsCallable(functions, "placesPlaceDetails");
      const rs: any = await fn({ placeId, sessionToken });
      const p = rs.data?.place;
      if (!p) return null;
      return {
        lat: typeof p.lat === "number" ? p.lat : null,
        lng: typeof p.lng === "number" ? p.lng : null,
        name: p.name || "",
        formatted: p.formatted || "",
        components: [],
      };
    } catch {
      return null;
    }
  }

  function selectAddressOption(o: AddressOption) {
    // Reset active index
    setAddrActiveIdx(-1);
    // For Google suggestion fetch details
    (async () => {
      if (o.provider === "google" && o.placeId) {
        const det = await fetchPlaceDetails(o.placeId, placesSession);
        if (det) {
          setAddressText(det.formatted || o.address || "");
          if (!locationName) setLocationName(det.name || o.name || "");
          if (typeof det.lat === "number") setLat(det.lat);
          if (typeof det.lng === "number") setLng(det.lng);
          // New session token for next interaction
          setPlacesSession(crypto?.randomUUID?.() || Math.random().toString(36).slice(2));
          return;
        }
      }
      // Fallback (internal or failed details)
      setAddressText(o.address);
      if (!locationName) setLocationName(o.name || "");
      if (typeof o.lat === "number") setLat(o.lat);
      if (typeof o.lng === "number") setLng(o.lng);
      setPlacesSession(crypto?.randomUUID?.() || Math.random().toString(36).slice(2));
    })();
    setAddrOpts([]);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Viewport-fit: constrain width/height, make inner content scrollable */}
      <DialogContent
        aria-describedby="create-event-desc"
        className="sm:max-w-xl w-[min(96vw,44rem)] max-h-[calc(100dvh-20px)] p-0 overflow-hidden"
      >
        <DialogHeader className="px-4 pt-4 pb-2 border-b border-white/10">
          <DialogTitle>{mode === "edit" ? "Edit Event" : "Create Event"}</DialogTitle>
          <DialogDescription id="create-event-desc">
            Create an event at an LGS or custom location. Pick visibility, time, and details.
          </DialogDescription>
        </DialogHeader>

        {/* Scrollable body */}
        <div className="max-h-[calc(100dvh-160px)] overflow-y-auto px-4 py-3 space-y-3">
          <div className="flex gap-2 items-center">
            <Button variant="outline" onClick={() => setFbOpen(true)}>Import from Facebook</Button>
          </div>

          {/* Cover image preview + defaults info */}
          <div className="space-y-1">
            <div className="rounded-lg overflow-hidden border border-white/10 bg-black/30">
              <img src={previewSrc} alt="Event cover preview" className="w-full h-40 object-cover" />
            </div>
            {!imageFile && (
              <div className="text-[11px] text-slate-300">
                No image selected. On save, a default will be used based on visibility:
                <span className="inline-flex items-center gap-2 ml-2 align-middle">
                  <img src={DEFAULT_PUBLIC_IMG} className="inline-block w-6 h-6 rounded border border-white/10" />
                  <img src={DEFAULT_PRIVATE_IMG} className="inline-block w-6 h-6 rounded border border-white/10" />
                </span>
              </div>
            )}
            <Input type="file" accept="image/*" onChange={(e) => setImageFile(e.target.files?.[0] ?? null)} className={inputCls} />
          </div>

          <Input placeholder="Event title" value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} />
          <Textarea placeholder="Details" value={details} onChange={(e) => setDetails(e.target.value)} className={inputCls} />
          <div className="flex gap-2">
            <Input type="datetime-local" value={startIso} onChange={(e) => setStartIso(e.target.value)} className={inputCls} />
            <Input type="datetime-local" value={endIso} onChange={(e) => setEndIso(e.target.value)} className={inputCls} />
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <label className={labelCls}>Visibility</label>
            <select className={inputCls} value={visibility} onChange={(e) => setVisibility(e.target.value as any)}>
              <option value="public">Public</option>
              <option value="private">Private (followers only)</option>
            </select>

            <label className={`${labelCls} ml-4`}>Event type</label>
            <select className={inputCls} value={eventType} onChange={(e) => setEventType(e.target.value as any)}>
              {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          {/* Formats (checkbox list) */}
          <div>
            <div className="text-sm text-slate-200 mb-1">Formats</div>
            <div className="flex flex-wrap gap-2">
              {EVENT_FORMATS.map((f) => (
                <label key={f} className="inline-flex items-center gap-1 text-xs">
                  <input type="checkbox" checked={formats.includes(f)} onChange={() => toggleFormat(f)} />
                  <span>{f}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Location */}
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <label className="text-sm">Location</label>
              <select
                className="bg-white/5 border border-white/10 rounded p-1 text-sm"
                value={locationType}
                onChange={(e) => setLocationType(e.target.value as any)}
              >
                {LOCATION_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>

            {locationType === "LGS" ? (
              <div className="space-y-2">
                <Input
                  placeholder="Search LGS by name"
                  value={lgsQuery}
                  onChange={(e) => setLgsQuery(e.target.value)}
                  className={inputCls}
                />
                <div className="text-[11px] text-slate-400">
                  Searches internal LGS first.
                </div>

                {/* Internal matches */}
                {lgsLoading ? (
                  <div className="text-xs text-slate-300">Searching…</div>
                ) : (
                  <>
                    {lgsRemote.length > 0 && (
                      <div className="border border-white/10 rounded">
                        <div className="px-2 py-1 text-[11px] text-slate-300 bg-white/5 border-b border-white/10">
                          Internal LGS
                        </div>
                        <div className="max-h-40 overflow-auto">
                          {lgsRemote.map((l) => (
                            <button
                              key={`int-${l.id}`}
                              type="button"
                              className="w-full text-left px-2 py-1 hover:bg-white/5 text-sm"
                              onClick={() => {
                                setLocationName(l.name);
                                setAddressText(l.address || "");
                                if (typeof l.lat === "number") setLat(l.lat as number);
                                if (typeof l.lng === "number") setLng(l.lng as number);
                                setEventHostUid(String(l.id));
                              }}
                            >
                              <div className="font-semibold text-indigo-100">{l.name}</div>
                              <div className="text-xs text-indigo-300">{l.address || "No address on file"}</div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Prompt to expand to WPN when no internal results */}
                    {lgsRemote.length === 0 && (
                      <div className="rounded border border-white/10 p-2 bg-white/5">
                        <div className="text-xs text-slate-200">
                          No internal LGS found for “{lgsQuery.trim()}”.
                        </div>
                        <div className="mt-2 flex items-center gap-2">
                          <Button
                            size="sm"
                            onClick={expandToWpn}
                            disabled={!wpnEnabled || wpnLoading || !lgsQuery.trim()}
                          >
                            {wpnLoading ? "Searching WPN…" : "Search Wizards Play Network"}
                          </Button>
                          {!wpnEnabled && (
                            <span className="text-[11px] text-amber-300">
                              Set VITE_WPN_FUNCTION_URL to enable WPN search.
                            </span>
                          )}
                        </div>
                        {/* WPN results list */}
                        {wpnExpanded && (
                          <>
                            {wpnError && <div className="mt-2 text-[11px] text-rose-300">{wpnError}</div>}
                            {wpnRemote.length > 0 && (
                              <div className="mt-2 border border-white/10 rounded">
                                <div className="px-2 py-1 text-[11px] text-slate-300 bg-white/5 border-b border-white/10">
                                  WPN results
                                </div>
                                <div className="max-h-48 overflow-auto">
                                  {wpnRemote.map((l) => (
                                    <button
                                      key={`wpn-${l.id}`}
                                      type="button"
                                      className="w-full text-left px-2 py-1 hover:bg-white/5 text-sm"
                                      onClick={async () => {
                                        setLocationName(l.name);
                                        setAddressText(l.address || "");
                                        if (typeof l.lat === "number") setLat(l.lat as number);
                                        if (typeof l.lng === "number") setLng(l.lng as number);
                                        try {
                                          const call = httpsCallable(functions, "createLgsFromWpn");
                                          const res: any = await call({
                                            name: l.name,
                                            address: l.address || null,
                                            lat: typeof l.lat === "number" ? l.lat : null,
                                            lng: typeof l.lng === "number" ? l.lng : null,
                                            website: l.website || null,
                                            phone: l.phone || null,
                                            email: l.email || null,
                                            wpnId: l.id || null,
                                            addressParts: l.addressParts || null,
                                          });
                                          const out = res?.data || {};
                                          if (out?.uid) setEventHostUid(String(out.uid));
                                        } catch (e) {
                                          console.warn("[CreateEvent] createLgsFromWpn failed", e);
                                        }
                                      }}
                                    >
                                      <div className="font-semibold text-indigo-100">{l.name}</div>
                                      <div className="text-xs text-indigo-300">{l.address || "No address on file"}</div>
                                      {(l.website || l.phone || l.email) && (
                                        <div className="mt-0.5 text-[11px] text-indigo-300/90">
                                          {[l.website, l.phone, l.email].filter(Boolean).join(" • ")}
                                        </div>
                                      )}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : (
              // Non-LGS: free-form address with autocomplete suggestions
              <div className="space-y-2 relative">
                <Input
                  placeholder="Location name (optional)"
                  value={locationName}
                  onChange={(e) => setLocationName(e.target.value)}
                  className={inputCls}
                />
                <Textarea
                  placeholder="Address"
                  value={addressText}
                  onChange={(e) => {
                    setAddressText(e.target.value);
                    setAddrActiveIdx(-1);
                  }}
                  onKeyDown={(e) => {
                    if (!addrOpts.length) return;
                    if (e.key === "ArrowDown") {
                      e.preventDefault();
                      setAddrActiveIdx(i => (i + 1) % addrOpts.length);
                    } else if (e.key === "ArrowUp") {
                      e.preventDefault();
                      setAddrActiveIdx(i => (i - 1 + addrOpts.length) % addrOpts.length);
                    } else if (e.key === "Enter") {
                      if (addrActiveIdx >= 0 && addrActiveIdx < addrOpts.length) {
                        e.preventDefault();
                        selectAddressOption(addrOpts[addrActiveIdx]);
                      }
                    } else if (e.key === "Escape") {
                      setAddrOpts([]);
                      setAddrActiveIdx(-1);
                    }
                  }}
                  className={inputCls + " relative z-10"}
                />

                {addrLoading && (
                  <div className="text-xs text-slate-300">Searching address…</div>
                )}

                {addrOpts.length > 0 && (
                  <div
                    ref={addrListRef}
                    className="absolute z-30 mt-1 max-h-56 w-full overflow-auto rounded-md border border-white/10 bg-slate-900/95 shadow-lg backdrop-blur-sm"
                  >
                    {addrOpts.map((o, i) => {
                      const active = i === addrActiveIdx;
                      return (
                        <button
                          key={o.id}
                          type="button"
                          className={`w-full text-left px-3 py-2 text-sm flex flex-col gap-0.5
                            ${active ? "bg-indigo-600/30" : "hover:bg-white/5"}`}
                          onMouseEnter={() => setAddrActiveIdx(i)}
                          onMouseDown={(e) => {
                            e.preventDefault(); // prevent blur
                            selectAddressOption(o);
                          }}
                        >
                          <span className="font-medium text-indigo-100 truncate">
                            {o.name}
                          </span>
                          <span className="text-[11px] text-indigo-300 line-clamp-2">
                            {o.address}
                          </span>
                          {o.provider === "google" && (
                            <span className="text-[10px] uppercase tracking-wide text-indigo-400/70">
                              Google
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}

                <div className="flex gap-2">
                  <Input
                    placeholder="Latitude"
                    value={lat}
                    onChange={(e) => setLat(Number(e.target.value) || 0)}
                    className={inputCls}
                  />
                  <Input
                    placeholder="Longitude"
                    value={lng}
                    onChange={(e) => setLng(Number(e.target.value) || 0)}
                    className={inputCls}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Recurrence */}
          <div className="space-y-2">
            <div className="text-sm text-slate-200">Single or recurring event</div>
            <div className="flex gap-3 text-sm">
              <label className="inline-flex items-center gap-1">
                <input type="radio" checked={recurrence === "single"} onChange={() => setRecurrence("single")} />
                Single
              </label>
              <label className="inline-flex items-center gap-1">
                <input type="radio" checked={recurrence === "weekly"} onChange={() => setRecurrence("weekly")} />
                Weekly
              </label>
              <label className="inline-flex items-center gap-1">
                <input type="radio" checked={recurrence === "monthly"} onChange={() => setRecurrence("monthly")} />
                Monthly
              </label>
            </div>

            {recurrence === "weekly" && (
              <div className="flex flex-wrap gap-2 text-xs">
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d, i) => (
                  <label key={i} className="inline-flex items-center gap-1">
                    <input type="checkbox" checked={!!weeklyDays[i]} onChange={() => toggleWeekday(i)} />
                    {d}
                  </label>
                ))}
              </div>
            )}

            {recurrence === "monthly" && (
              <div className="flex items-center gap-2 text-sm">
                <span>Day of month</span>
                <Input type="number" min={1} max={31} value={monthlyDay} onChange={(e) => setMonthlyDay(Math.max(1, Math.min(31, Number(e.target.value) || 1)))} className="w-20" />
              </div>
            )}
          </div>
        </div>

        {/* Footer (sticks to bottom of dialog) */}
        <div className="px-4 pb-4 pt-2 border-t border-white/10 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={createEvent} disabled={saving || !title || !startIso}>
            {saving ? (mode === "edit" ? "Saving..." : "Creating...") :
              (mode === "edit" ? "Save Changes" :
                (recurrence === "single" ? "Create" : "Create series"))}
          </Button>
        </div>

        <FacebookImportDialog
          open={fbOpen}
          onOpenChange={setFbOpen}
          onImported={(imp) => {
            if (imp.title) setTitle(imp.title);
            if (imp.details) setDetails(imp.details);
            if (imp.startIso) setStartIso(imp.startIso);
            if (imp.endIso) setEndIso(imp.endIso);
            if (imp.url) setSourceUrl(imp.url);
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

function coerceDateAny(v: any): Date | null {
  if (!v) return null;
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
  if (typeof v === "number") {
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof v === "string") {
    const t = Date.parse(v);
    if (Number.isFinite(t)) {
      const d = new Date(t);
      return isNaN(d.getTime()) ? null : d;
    }
    return null;
  }
  // Firestore Timestamp (compat)
  if (v?.toDate && typeof v.toDate === "function") {
    try {
      const d = v.toDate();
      return d instanceof Date && !isNaN(d.getTime()) ? d : null;
    } catch { return null; }
  }
  // Plain object { seconds, nanoseconds }
  if (typeof v === "object" && typeof v.seconds === "number") {
    const ms = v.seconds * 1000 + Math.floor((v.nanoseconds || 0) / 1e6);
    const d = new Date(ms);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

function toDateTimeLocalInput(d: Date): string {
  // Returns YYYY-MM-DDTHH:MM in local time (no seconds)
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

